import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { CarCard } from "@/components/CarCard";
import { useLang } from "@/context/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Inventory = () => {
  const { t } = useLang();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState("all");
  const [transmission, setTransmission] = useState("all");
  const [fuel, setFuel] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    document.title = "Car Inventory | Shree Peteanjaneya Swamy Enterprises";
    api.get("/cars").then((res) => setCars(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const brands = useMemo(() => [...new Set(cars.map((c) => c.brand))].sort(), [cars]);
  const fuels = useMemo(() => [...new Set(cars.map((c) => c.fuel_type))].sort(), [cars]);

  const filtered = useMemo(() => {
    let out = cars.filter((c) =>
      (brand === "all" || c.brand === brand) &&
      (transmission === "all" || c.transmission === transmission) &&
      (fuel === "all" || c.fuel_type === fuel)
    );
    if (sort === "price-low") out = [...out].sort((a, b) => a.price - b.price);
    else if (sort === "price-high") out = [...out].sort((a, b) => b.price - a.price);
    return out;
  }, [cars, brand, transmission, fuel, sort]);

  const selCls = "bg-[#141414] border-white/20 text-white h-11 min-w-[150px] data-[placeholder]:text-gray-500";

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-24 min-h-screen">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-xs tracking-[0.2em] uppercase text-[#FF3B30] font-bold">{t("inv.label")}</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-white mt-2">{t("inv.title")}</h1>
        <p className="text-gray-400 mt-3">{filtered.filter((c) => !c.is_sold).length} {t("inv.available")}</p>
      </motion.div>

      <div className="flex flex-wrap items-center gap-3 mt-8 mb-10 p-4 bg-[#0d0d0d] border border-white/10 rounded-xl">
        <span className="flex items-center gap-2 text-gray-400 text-sm font-semibold mr-2"><SlidersHorizontal size={16} /> {t("inv.filters")}</span>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className={selCls} data-testid="filter-brand"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10 text-white">
            <SelectItem value="all">{t("inv.allBrands")}</SelectItem>
            {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={transmission} onValueChange={setTransmission}>
          <SelectTrigger className={selCls} data-testid="filter-transmission"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10 text-white">
            <SelectItem value="all">{t("inv.allTrans")}</SelectItem>
            <SelectItem value="Manual">Manual</SelectItem>
            <SelectItem value="Automatic">Automatic</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fuel} onValueChange={setFuel}>
          <SelectTrigger className={selCls} data-testid="filter-fuel"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10 text-white">
            <SelectItem value="all">{t("inv.allFuel")}</SelectItem>
            {fuels.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className={selCls} data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10 text-white">
            <SelectItem value="newest">{t("inv.newest")}</SelectItem>
            <SelectItem value="price-low">{t("inv.priceLow")}</SelectItem>
            <SelectItem value="price-high">{t("inv.priceHigh")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-xl py-24 text-center text-gray-400" data-testid="no-cars">
          {t("inv.noMatch")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
        </div>
      )}
    </div>
  );
};

export default Inventory;
